import React from "react";
import { Helmet } from "react-helmet";
import { domainName, officeContactDetails, socialLinks } from "config/config";
import { WEBSITE_NAME } from "constant/footerConst";

const SEO = ({ title, description, link, imageUrl, articleSchema }) => {
  const DEFAULT_OG_IMAGE = `${domainName.fullLink}/assets/images/og-logo.png`;
  const image = imageUrl || DEFAULT_OG_IMAGE;

  // officeContactDetails is an array, take the first one for the main schema
  const contact = officeContactDetails[0];
  const sameAsLinks = socialLinks.map((item) => item.link);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: WEBSITE_NAME,
    url: domainName.fullLink,
    image: image,
    telephone: contact.phone,
    email: contact.emailId,
    address: {
      "@type": "PostalAddress",
      streetAddress: `${contact.addressFirstLine}${
        contact.addressSecondLine ? ", " + contact.addressSecondLine : ""
      }`,
      addressLocality: contact.city,
      postalCode: contact.pinCode,
      addressCountry: "IN",
    },
    sameAs: sameAsLinks,
  };

  /**
   * AEO — BlogPosting schema
   * Injected only when articleSchema prop is provided.
   * Helps AI search engines (Google SGE, Bing Copilot, Perplexity) surface
   * article content as direct answers.
   */
  const blogPostingSchema = articleSchema
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: articleSchema.headline || title,
        description: articleSchema.description || description,
        image: articleSchema.image || image,
        url: articleSchema.url || link,
        datePublished: articleSchema.datePublished || "",
        dateModified:
          articleSchema.dateModified || articleSchema.datePublished || "",
        author: {
          "@type": "Organization",
          name: articleSchema.author || WEBSITE_NAME,
          url: domainName.fullLink,
        },
        publisher: {
          "@type": "Organization",
          name: WEBSITE_NAME,
          logo: {
            "@type": "ImageObject",
            url: DEFAULT_OG_IMAGE,
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": articleSchema.url || link,
        },
        keywords: articleSchema.keywords || "",
        inLanguage: articleSchema.inLanguage || "en-IN",
      }
    : null;

  return (
    <Helmet>
      <meta charSet="utf-8" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={link} />
      <meta name="author" content="kalyanawaran - A Matrimony website." />
      <meta name="image" content={image} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Schema.org markup for Local Business */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      {/* AEO — BlogPosting schema (only on article pages) */}
      {blogPostingSchema && (
        <script type="application/ld+json">
          {JSON.stringify(blogPostingSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
